import { action, computed } from 'mobx';
import { GTMReporters } from 'components/HOCs/withGtmLogger';
import { SubscriptionPageProps } from './SubscriptionPageProps';
import { ServicePendingChangesInfo } from 'types';
import { PremiumCartApiClient } from '@wix/premium-cart-sdk';
import { paymentCycleToCycle } from '../../utils/mappers/paymentCycleToCycle.mapper';
import { ProductTypeIds } from '@wix/package-picker-common';
import { SentryLogger } from 'services/SentryLogger';
import { IHttpClient } from '@wix/http-client';
import { AppFlow } from 'enums';
import i18next from 'i18next';
import { PaymentCycle_BIMap } from 'services/BiLogger/BiLoggerTypes';

export class SubscriptionPageController {
  constructor(private readonly props: SubscriptionPageProps) {
    void this.notifyGTM();
  }

  notifyGTM = async () => {
    const { storage } = this.props.rootStore.appStore;
    storage.getCycleSectionViewed().then(
      () => {},
      async () => {
        await storage.setCycleSectionViewed();
        this.props.reportGTM(GTMReporters.CycleSelector);
      },
    );
  };

  getFooterKey = () => {
    const { pricesIncludeTax, isEligibleForRevShare, isResellerOffering } =
      this.props.rootStore.appStore;

    if (isEligibleForRevShare) {
      return 'studio_package_picker.billing_cycle_revshare.disclaimer';
    }

    if (isResellerOffering) {
      return pricesIncludeTax
        ? 'page_footer.reseller_sale.cycle_disclaimer'
        : 'page_footer.reseller_sale.cycle_disclaimer_no_tax';
    }

    return pricesIncludeTax
      ? 'page_footer.subscription_disclaimer_no_vat'
      : 'page_footer.subscription_disclaimer_applicable_tax';
  };

  @action
  closeNoWebsiteModal = () => {
    this.props.rootStore.appStore.setNoWebsiteModal(false);
  };

  @action
  openNoWebsiteModal = () => {
    this.props.rootStore.appStore.setNoWebsiteModal(true);
  };

  @action
  onCycleSelect = () => {
    const { removeUnloadListener, appContext, currentProduct, currentCycle } =
      this.props.rootStore.appStore;
    const { eventAggregator } = appContext;

    appContext.biLogger.logContinueToCheckout({
      productGuid: currentProduct.guid,
      siteId: appContext.siteGuid,
      cartFlow: AppFlow.CLASSIC,
      cycleid: PaymentCycle_BIMap[currentCycle],
      eventAggregator,
    });

    const { onCycleSelect } = this.props;

    if (appContext.siteGuid) {
      onCycleSelect?.();
      return;
    }
    removeUnloadListener();
    this.openNoWebsiteModal();
  };

  @action
  onCycleSelectToCart = async (httpClient: IHttpClient | null | undefined) => {
    const {
      currentCycle,
      currentProduct,
      baseUrl,
      removeUnloadListener,
      appContext,
    } = this.props.rootStore.appStore;
    const { eventAggregator } = appContext;

    appContext.biLogger.logContinueToCheckout({
      productGuid: currentProduct.guid,
      siteId: appContext.siteGuid,
      cartFlow: AppFlow.CART,
      cycleid: PaymentCycle_BIMap[currentCycle],
      eventAggregator,
    });

    const searchParams = new URLSearchParams(window.location.search);

    try {
      if (!httpClient) {
        throw new Error(
          'cannot upsertLineItemToActiveCart - httpClient is not defined',
        );
      }

      const upsertLineItemToActiveCart = PremiumCartApiClient.queries({
        httpClient,
      }).upsertLineItemToActiveCart;

      await upsertLineItemToActiveCart({
        lineItem: {
          cycle: paymentCycleToCycle(currentCycle),
          productInfo: {
            productId: currentProduct.guid,
            productTypeId: ProductTypeIds.PremiumPlans,
          },
        },
      });
    } catch (e) {
      searchParams.set(
        'failedProduct',
        i18next.t(
          `premium-cart.checkout.page-error.product.not.added-premium.plan`,
        ),
      );
      SentryLogger.logError(e);
    } finally {
      if (searchParams.has('siteGuid')) {
        searchParams.set('msid', searchParams.get('siteGuid')!);
        searchParams.delete('siteGuid');
      }

      removeUnloadListener();
      window.location.assign(`${baseUrl}cart?${searchParams.toString()}`);
    }
  };

  @computed
  get serviceHasPendingChanges(): boolean {
    return !!this.props.rootStore.appStore.appContext.servicePendingChangesInfo
      ?.servicePendingChanges?.billingCycle?.billingCycle;
  }

  @computed
  get servicePendingChangesInfo(): ServicePendingChangesInfo | undefined {
    return this.props.rootStore.appStore.appContext.servicePendingChangesInfo;
  }
}
